import { useContext, useMemo } from 'react';
import intlInstance from 'react-intl-universal';
import { I18nContext } from './context';

/**
 * intl - 静态获取文案
 *
 * 用于非 React 环境（工具函数、Service、Store）
 *
 * @example
 * ```ts
 * import { intl } from '@wegic/i18n-core';
 *
 * function formatError(code: string) {
 *   return intl.get(`error.${code}`);
 * }
 * ```
 */
function getInjectI18n(key: string, params: { [key: string]: any }) {
  const text = intlInstance.get(key);
  if (!text) {
    return key;
  }
  const arr = text.split(/\{([^}]*)\}/);
  const keys = Object.keys(params);
  return arr
    .map((item) => (keys.includes(item) ? params[item] : item))
    .filter((item) => item !== '');
}

export const intl = Object.assign({}, intlInstance, {
  get: (key: string, variables?: Record<string, any>) => {
    return intlInstance.get(key, variables) || key;
  },
  inject: getInjectI18n,
});

/**
 * useTranslation - 响应式获取文案（Hook）
 *
 * 特点：
 * - 语言切换时自动触发重渲染
 * - 返回 t 函数和当前 locale
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { t, locale } = useTranslation();
 *   return <div>{t('hello.world')}</div>;
 * }
 * ```
 */
export function useTranslation() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }

  const { locale, setLocale } = context;

  const t = useMemo(() => {
    return (key: string, variables?: Record<string, any>) => {
      return intl.get(key, variables);
    };
  }, [locale]);

  return {
    t,
    locale,
    setLocale,
    intl,
  };
}
